import React from "react";
import { useNavigate } from "react-router-dom";
import gif from "../assets/images/webp/giphy.webp";
import { usePublicOrder } from "../context/PublicOrderContext";
import { toast } from "react-toastify";
import api from "../utils/api";

const CustomerNavbar = ({ cartCount, slug, onMenuClick, onCartClick }) => {
  const navigate = useNavigate();
  const { orderInfo, restaurant, resetSession, participantsCount } = usePublicOrder();

  const handleLogoClick = () => {
    if (!orderInfo) {
      navigate(`/r/${slug}`);
      return;
    }

    toast.warning(
      ({ closeToast }) => {
        let inputRef = null;

        return (
          <div className="flex flex-col gap-3 w-[260px]">
            <p className="text-sm font-semibold">Cancel this order?</p>

            <input
              type="text"
              placeholder="Reason (optional)"
              className="border rounded px-2 py-1 text-sm"
              ref={(el) => (inputRef = el)}
            />

            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => closeToast()}
                className="px-3 py-1 text-sm bg-gray-200 rounded"
              >
                No
              </button>

              <button
                onClick={async () => {
                  try {
                    const reason = inputRef?.value || "";

                    await api.patch(
                      `/api/public/restaurant/${slug}/order/${orderInfo._id}/cancel`,
                      { reason },
                    );

                    resetSession();
                    closeToast();
                    navigate(`/r/${slug}`);
                  } catch (err) {
                    toast.error(
                      err?.response?.data?.message || "Failed to cancel order",
                    );
                  }
                }}
                className="px-3 py-1 text-sm bg-red-500 text-white rounded"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        );
      },
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
      },
    );
  };

  return (
    <nav className="bg-white w-full shadow-md px-2 sm:px-4 py-1.5 sm:py-2 flex items-center justify-between fixed top-0 z-40">
      {/* LEFT SECTION */}
      <div className="flex items-center gap-1.5 sm:gap-3">
        {/* 🍔 Hamburger (Mobile Only) */}
        <button onClick={onMenuClick} className="lg:hidden text-lg sm:text-xl pl-1">
          ☰
        </button>

        {/* Logo + Name */}
        <div
          className="flex items-center gap-1.5 sm:gap-4 cursor-pointer"
          onClick={handleLogoClick}
        >
          <img
            src={restaurant?.logo || gif}
            alt="Store Logo"
            className="w-8 h-8 sm:w-14 sm:h-14 object-cover rounded-full shadow-sm"
          />
          <div
            className={`marquee !w-[75px] sm:!w-[150px] ${restaurant?.name.length > 15 ? "scroll-enabled" : ""}`}
          >
            <div className="track">
              <span className="!text-[13px] sm:!text-xl">
                {restaurant?.name || "Cloud Billing"}&nbsp;&nbsp;&nbsp;&nbsp;
              </span>
              {restaurant?.name.length > 15 && (
                <span className="!text-[13px] sm:!text-xl">
                  {restaurant?.name || "Cloud Billing"}&nbsp;&nbsp;&nbsp;&nbsp;
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT SECTION */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* 📞 Call */}
        {restaurant?.mobile && (
          <a
            href={`tel:${restaurant?.mobile}`}
            className="hidden md:flex items-center gap-2 text-gray-700 hover:text-teal-600"
          >
            <i className="fas fa-phone-alt"></i>
            <span className="text-sm font-medium">{restaurant?.mobile}</span>
          </a>
        )}

        {/* Order Type Badge */}
        {orderInfo && (
          <div className="flex items-center gap-1 sm:gap-2 text-[11px] sm:text-sm font-semibold text-gray-800">
            {orderInfo.orderType === "DIRECT" ? (
              <>🛍 <span className="hidden sm:inline">Parcel</span> {orderInfo.parcelToken}</>
            ) : (
              <>
                {sessionStorage.getItem("table_lock_active") === "true" && (
                  <span title="Session Locked to Table" className="text-[9px] sm:text-xs text-teal-600 bg-teal-50 px-1 py-0.5 rounded-md border border-teal-200 flex items-center gap-0.5 mr-0.5 sm:mr-1 animate-pulse">
                    🔒 <span className="hidden sm:inline">Locked</span>
                  </span>
                )}
                {orderInfo.sectionType === "TABLE" ? "🪑 T" : "🏨 R"}
                {orderInfo.unitNumber}

                {orderInfo.sectionType === "ROOM" &&
                  orderInfo.roomType &&
                  orderInfo.roomType !== "NA" && (
                    <span className="hidden sm:inline text-[9px] sm:text-xs px-1 sm:px-2 py-0.5 rounded-full bg-gray-200 text-gray-700">
                      {orderInfo.roomType === "AC" ? "❄️ AC" : "🌤 Non-AC"}
                    </span>
                  )}

                <span className="hidden sm:inline text-[10px] sm:text-xs text-gray-500">
                  ({orderInfo.floorName})
                </span>
              </>
            )}
          </div>
        )}

        {/* Group Ordering Counter Badge */}
        {participantsCount > 1 && (
          <div className="flex items-center gap-1 sm:gap-1.5 bg-green-50 text-green-700 border border-green-200 px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold">
            <span className="relative flex h-1.5 w-1.5 sm:h-2 sm:w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 sm:h-2 sm:w-2 bg-green-500"></span>
            </span>
            <span>👥 {participantsCount} <span className="hidden sm:inline">Active</span></span>
          </div>
        )}

        {/* 🧾 View Bill */}
        {orderInfo && (
          <button
            onClick={() => navigate(`/bill/${orderInfo._id}`)}
            className="flex items-center gap-1 px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-purple-600 to-indigo-650 text-white text-[10px] sm:text-xs font-extrabold rounded-full transition shadow-md hover:scale-[1.01]"
          >
            <span>🧾 <span className="hidden sm:inline">Bill / Pay</span></span>
          </button>
        )}

        {/* 🛒 Cart */}
        <div onClick={onCartClick} className="relative cursor-pointer mr-1">
          <i className="fas fa-shopping-cart text-lg sm:text-xl"></i>

          {cartCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 rounded-full">
              {cartCount}
            </span>
          )}
        </div>
      </div>
    </nav>
  );
};

export default CustomerNavbar;
